/**
 * Frame quality metrics on grayscale buffers — ARCHITECTURE.md §8 (client side).
 * All functions are pure so they can be unit-tested without a browser.
 */

export interface GrayFrame {
  data: Uint8Array;
  width: number;
  height: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function cropGray(frame: GrayFrame, rect: Rect): GrayFrame {
  const x0 = Math.max(0, Math.floor(rect.x));
  const y0 = Math.max(0, Math.floor(rect.y));
  const w = Math.min(frame.width - x0, Math.floor(rect.w));
  const h = Math.min(frame.height - y0, Math.floor(rect.h));
  const out = new Uint8Array(Math.max(0, w) * Math.max(0, h));
  for (let y = 0; y < h; y++) {
    out.set(frame.data.subarray((y0 + y) * frame.width + x0, (y0 + y) * frame.width + x0 + w), y * w);
  }
  return { data: out, width: Math.max(0, w), height: Math.max(0, h) };
}

/** RGBA ImageData bytes → luma (BT.601). */
export function rgbaToGray(rgba: Uint8ClampedArray, width: number, height: number): GrayFrame {
  const out = new Uint8Array(width * height);
  for (let i = 0, p = 0; i < out.length; i++, p += 4) {
    out[i] = (rgba[p]! * 77 + rgba[p + 1]! * 150 + rgba[p + 2]! * 29) >> 8;
  }
  return { data: out, width, height };
}

/** Variance of the 4-neighbour Laplacian — §8 relative sharpness signal. */
export function laplacianVariance(frame: GrayFrame): number {
  const { data, width: w, height: h } = frame;
  if (w < 3 || h < 3) return 0;
  const n = (w - 2) * (h - 2);
  let sum = 0;
  let sumSq = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const lap = data[i - w]! + data[i - 1]! + data[i + 1]! + data[i + w]! - 4 * data[i]!;
      sum += lap;
      sumSq += lap * lap;
    }
  }
  const mean = sum / n;
  return sumSq / n - mean * mean;
}

export interface ExposureStats {
  mean: number;
  contrast: number;
  underExposureRatio: number;
  overExposureRatio: number;
}

export function exposureStats(frame: GrayFrame): ExposureStats {
  const { data } = frame;
  if (data.length === 0) return { mean: 0, contrast: 0, underExposureRatio: 1, overExposureRatio: 0 };
  let sum = 0;
  let sumSq = 0;
  let under = 0;
  let over = 0;
  for (let i = 0; i < data.length; i++) {
    const v = data[i]!;
    sum += v;
    sumSq += v * v;
    if (v < 16) under++;
    if (v > 250) over++;
  }
  const mean = sum / data.length;
  return {
    mean,
    contrast: Math.sqrt(Math.max(0, sumSq / data.length - mean * mean)),
    underExposureRatio: under / data.length,
    overExposureRatio: over / data.length,
  };
}

/** Mean absolute difference between two same-sized frames — §8 motionScore. */
export function motionScore(a: GrayFrame, b: GrayFrame): number {
  if (a.data.length !== b.data.length || a.data.length === 0) return 255;
  let sum = 0;
  for (let i = 0; i < a.data.length; i++) sum += Math.abs(a.data[i]! - b.data[i]!);
  return sum / a.data.length;
}

export interface GlareStats {
  /** Fraction of near-saturated pixels (≥250). */
  ratio: number;
  /** Largest connected saturated blob as a fraction of the ROI (8×8 block growth). */
  maxBlockRatio: number;
}

/**
 * Specular-glare detection: overall saturation ratio plus the largest compact blob of
 * saturated 8×8 blocks (a compact blob is glare; scattered bright pixels usually are not).
 */
export function glareStats(frame: GrayFrame): GlareStats {
  const { data, width: w, height: h } = frame;
  if (data.length === 0) return { ratio: 0, maxBlockRatio: 0 };
  let saturated = 0;
  const bw = Math.ceil(w / 8);
  const bh = Math.ceil(h / 8);
  const blockHot = new Uint8Array(bw * bh);
  const blockCounts = new Uint16Array(bw * bh);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[y * w + x]! >= 250) {
        saturated++;
        blockCounts[(y >> 3) * bw + (x >> 3)]!++;
      }
    }
  }
  for (let i = 0; i < blockHot.length; i++) blockHot[i] = blockCounts[i]! >= 20 ? 1 : 0;

  // Flood fill over hot blocks to find the largest blob.
  let maxBlob = 0;
  const seen = new Uint8Array(bw * bh);
  const stack: number[] = [];
  for (let start = 0; start < blockHot.length; start++) {
    if (!blockHot[start] || seen[start]) continue;
    let size = 0;
    stack.push(start);
    seen[start] = 1;
    while (stack.length) {
      const i = stack.pop()!;
      size++;
      const bx = i % bw;
      const by = (i / bw) | 0;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const nx = bx + dx;
        const ny = by + dy;
        if (nx < 0 || ny < 0 || nx >= bw || ny >= bh) continue;
        const ni = ny * bw + nx;
        if (blockHot[ni] && !seen[ni]) {
          seen[ni] = 1;
          stack.push(ni);
        }
      }
    }
    maxBlob = Math.max(maxBlob, size);
  }

  return {
    ratio: saturated / data.length,
    maxBlockRatio: (maxBlob * 64) / data.length,
  };
}

/** Sobel gradient magnitude mean over a region — edge density for card presence. */
export function edgeDensity(frame: GrayFrame): number {
  const { data, width: w, height: h } = frame;
  if (w < 3 || h < 3) return 0;
  let strong = 0;
  const n = (w - 2) * (h - 2);
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const gx =
        -data[i - w - 1]! + data[i - w + 1]! -
        2 * data[i - 1]! + 2 * data[i + 1]! -
        data[i + w - 1]! + data[i + w + 1]!;
      const gy =
        -data[i - w - 1]! - 2 * data[i - w]! - data[i - w + 1]! +
        data[i + w - 1]! + 2 * data[i + w]! + data[i + w + 1]!;
      if (gx * gx + gy * gy > 128 * 128) strong++;
    }
  }
  return strong / n;
}

export interface CardPresence {
  present: boolean;
  sidesDetected: number;
  innerContrast: number;
}

/**
 * Guided-box card presence (§4): the user aligns the card inside the fixed overlay, so a
 * present-and-aligned card puts strong edges inside all four border bands of the box and
 * printed texture inside the inner region. This does not localize a free card polygon —
 * that stronger detector ships with the native SDKs (WEB_LOW_ASSURANCE, §2).
 */
export function detectCardInBox(
  frame: GrayFrame,
  box: Rect,
  opts: { borderBandRatio: number; edgeDensityMin: number; sidesMin: number; innerContrastMin: number },
): CardPresence {
  const band = Math.max(3, Math.round(Math.min(box.w, box.h) * opts.borderBandRatio));
  const bands: Rect[] = [
    { x: box.x - band, y: box.y - band, w: box.w + 2 * band, h: 2 * band }, // top
    { x: box.x - band, y: box.y + box.h - band, w: box.w + 2 * band, h: 2 * band }, // bottom
    { x: box.x - band, y: box.y, w: 2 * band, h: box.h }, // left
    { x: box.x + box.w - band, y: box.y, w: 2 * band, h: box.h }, // right
  ];
  let sides = 0;
  for (const b of bands) {
    if (edgeDensity(cropGray(frame, b)) >= opts.edgeDensityMin) sides++;
  }
  const innerMargin = 0.2;
  const inner = cropGray(frame, {
    x: box.x + box.w * innerMargin,
    y: box.y + box.h * innerMargin,
    w: box.w * (1 - 2 * innerMargin),
    h: box.h * (1 - 2 * innerMargin),
  });
  const innerContrast = exposureStats(inner).contrast;
  return {
    present: sides >= opts.sidesMin && innerContrast >= opts.innerContrastMin,
    sidesDetected: sides,
    innerContrast,
  };
}
